import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { ServiceHealthStatusEnum } from '@gridcon-energy/schaltungstool-backend'
import type { ServicesHealthCheck } from '@gridcon-energy/schaltungstool-backend'
import { injectBackendClient } from '~/api/backendClients'
import useFetchData from '~/composables/common/useFetchData'
import { useStorage, useTimeoutPoll } from '@vueuse/core'
import { Notify } from '~/lib/notify'
import { useI18n } from 'vue-i18n'

const FATAL_ERROR = 'FATAL_ERROR'
type AppHealthyStatus = boolean | null | typeof FATAL_ERROR

export function useServiceHealthCheck() {
  const backend = injectBackendClient()
  const statusResults = ref<ServicesHealthCheck | null>(null)
  const interval = ref(30_000)
  const { t } = useI18n()

  const lastReportedStatus = useStorage<AppHealthyStatus>('health-check-status', null)

  const fetch = useFetchData.immediate(async () => {
    const response = await backend.app.healthCheckServices()
    statusResults.value = response.data
  })

  const { pause, resume } = useTimeoutPoll(
    async () => {
      await fetch.refresh()
    },
    interval,
    { immediate: true },
  )
  onMounted(resume)
  onUnmounted(pause)

  /**
   * Whether all services are healthy.
   */
  const allHealthy = computed((): AppHealthyStatus => {
    if (fetch.state.error) {
      return FATAL_ERROR
    }
    if (statusResults.value == null) {
      return null
    }

    return Object.values(statusResults.value).every((service) => service.status === ServiceHealthStatusEnum.Healthy)
  })

  /**
   * Notify the user if the status changed.
   */
  watch(allHealthy, (newValue, oldValue) => {
    if (oldValue === null && newValue === true) return // default init
    if (newValue === oldValue) return // no change
    if (newValue == null) return // undefined
    if (lastReportedStatus.value === newValue) return // the status did not change to the last reported one
    lastReportedStatus.value = newValue

    const message = forCurrentStatus(
      t('ServiceStatusAllHealthyRestored'),
      t('ServiceStatusSomeUnhealthy'),
      t('ServiceStatusFatal'),
      t('ServiceStatusUndefined'),
    )

    Notify(message, {
      type: forCurrentStatus('success', 'warning', 'error', 'info'),
      lib: {
        position: 'top-right',
      },
    })
  })

  /**
   * Based on the current status, return the correct value.
   */
  function forCurrentStatus<T extends string>(success: T, warning: T, error: T, none: T): T {
    if (allHealthy.value === true) {
      return success
    }
    if (allHealthy.value === false) {
      return warning
    }
    if (allHealthy.value === FATAL_ERROR) {
      return error
    }
    return none
  }

  return {
    interval,
    fetch,
    allHealthy: allHealthy,
    forCurrentStatus: forCurrentStatus,
    statusResults: statusResults,
  }
}
